'use client';

import { Divider } from '@nextui-org/react';
import { useSearchParams } from 'next/navigation';
import React, { useState } from 'react';

import { CloseLineHeaderIcon } from '@/assets';
import ListBarMarket from '@/components/list-bar/ListBarMarket';
import NoData from '@/components/NoData';
import Pagination from '@/components/pagination';
import RenderWhen from '@/components/RenderWhen';
import FilterMarketPlace from '@/contents/marketplace/Categories/Artwork/FilterMarketPlace';
import { useRouter } from '@/provider/RouterProvider';

import { CategoryKey } from '../Trending/constants';
import ListMarket from './ListMarket';
import SkeletonMarketList from './Skeleton';
import useMarketplace, { PAGE_SIZE } from './useMarketplace';

const MarketArtwork: React.FC = () => {
  const [openFilter, setOpenFilter] = useState(false);
  const { list, loading, totalPage } = useMarketplace();
  const searchParam = useSearchParams();
  const page = searchParam.get('page') ?? 1;
  const searchPlaceholder = `Search in ${
    searchParam.get('assetCategory') || (searchParam.get('prompt') ? 'prompt' : 'all')
  }`;
  const hasClearAll =
    searchParam.has('name') ||
    searchParam.has('createdAt') ||
    searchParam.has('prompt') ||
    searchParam.has('priceFrom') ||
    searchParam.has('priceTo') ||
    searchParam.has('modelIds') ||
    searchParam.has('tagIds') ||
    searchParam.has('tags') ||
    searchParam.has('isPlus') ||
    searchParam.has('assetCategory');

  const router = useRouter();

  const handleClearFilter = () => {
    setOpenFilter(false);
    router.push(
      `/marketplace?category=${CategoryKey.artwork}`,
      { scroll: false },
      { noLoadBar: true },
    );
  };

  return (
    <div className="px-[40px] md:px-[20px]">
      <div className="mt-4 flex flex-col">
        <ListBarMarket
          handleClearFilter={handleClearFilter}
          setOpenFilter={setOpenFilter}
          openFilter={openFilter}
          searchPlaceholder={searchPlaceholder}
          hasClearAll={hasClearAll}
        />
      </div>
      <div className="py-10 lg:h-[100%] lg:py-5">
        <RenderWhen>
          <RenderWhen.If isTrue={list?.length === 0 && loading}>
            <SkeletonMarketList />
          </RenderWhen.If>
          <RenderWhen.If isTrue>
            <div className="flex w-full">
              <div
                aria-expanded={openFilter}
                className="w-0 overflow-y-auto opacity-0 duration-300 aria-expanded:w-[320px] aria-expanded:opacity-100 md:fixed md:inset-0 md:z-99 md:bg-white md:pr-0 aria-expanded:md:w-full"
              >
                <div className="hidden w-full items-center justify-between p-4 md:flex">
                  <span className="text-20 font-semibold">Filters</span>
                  <CloseLineHeaderIcon onClick={() => setOpenFilter(false)} />
                </div>
                <Divider className="hidden w-full bg-gray-100 md:block" />
                {openFilter && (
                  <FilterMarketPlace
                    handleClearFilter={handleClearFilter}
                    setOpenFilter={setOpenFilter}
                  />
                )}
              </div>

              <div
                aria-expanded={openFilter}
                className="w-full aria-expanded:w-[calc(100%-320px)] aria-expanded:duration-300 md:!w-full"
              >
                <RenderWhen.If isTrue={list?.length > 0}>
                  <div className="px-[10px]">
                    <ListMarket openFilter={openFilter} nftMarketList={list} />
                  </div>
                  <div className="grid justify-items-center">
                    <Pagination
                      className="mt-5 sm:mb-5"
                      startPage={Number(page)}
                      total={totalPage}
                      pageSize={PAGE_SIZE}
                    />
                  </div>
                </RenderWhen.If>
                <RenderWhen.If isTrue={list.length === 0 && !loading}>
                  <NoData />
                </RenderWhen.If>
              </div>
            </div>
          </RenderWhen.If>
        </RenderWhen>
      </div>
    </div>
  );
};

export default MarketArtwork;
